import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import { ref } from 'vue';
import i18n from '@/i18n';

const _aiContext = ref(null);

// Nombre de icono por KPI — lo resuelve `AppIcon` (Phosphor).
//
// Antes cada entrada apuntaba a un ARCHIVO `.svg` que se pintaba con `<img>`, con
// el color horneado adentro. Eso los dejaba fuera del sistema por partida doble:
// el CSS no puede tintar un `<img>`, y ninguna auditoría de estilos los ve —
// `getComputedStyle` devuelve el color HEREDADO, o sea que los certificaba como
// en-token mientras en pantalla eran azul CornflowerBlue.
//
// La CLAVE sigue viniendo del backend; lo que cambia es que el catálogo ahora
// habla en nombres, que es para lo que existe el resolver.
//
// `pillar` recupera —y mejora— algo que los archivos SVG ya codificaban con el
// color horneado: medido sobre los originales, verde = ambiental y azul = todo
// lo demás, consistente en las 46 entradas. Ese azul metía en la misma bolsa lo
// social, lo cultural y proveedores, que el sistema de pilares de la app sí
// separa. Así que no es restaurar el código viejo, es subirlo de 2 categorías a
// 4 (contour · ochre · plum · accent).
//
// ⚠️ El pilar tiñe SOLO el icono. El número se queda en tinta — un número de
// color vuelve a mezclar lo categórico con lo funcional, que es justo lo que se
// sacó del landing de Monitor.
export const KPI_CONFIG = {
    // Initiatives
    ecosystemRestoration:    { icon: 'tree', pillar: 'environmental' },
    ecosystemsProtected:     { icon: 'shield', pillar: 'environmental' },
    wasteDiverted:           { icon: 'recycle', pillar: 'environmental' },
    treesPlanted:            { icon: 'plant', pillar: 'environmental' },
    cleanEnergyAdoption:     { icon: 'zap', pillar: 'environmental' },
    marineCoastalProtection: { icon: 'droplet', pillar: 'environmental' },
    beachCleanups:           { icon: 'waves', pillar: 'environmental' },
    peopleTrained:           { icon: 'book-open', pillar: 'social' },
    healthAccess:            { icon: 'first-aid', pillar: 'social' },
    communityInfrastructure: { icon: 'users-three', pillar: 'social' },
    localEconomicInvestment: { icon: 'wallet', pillar: 'social' },
    socialInclusionReach:    { icon: 'hand-heart', pillar: 'social' },
    waterSanitationAccess:   { icon: 'drop-half', pillar: 'social' },
    foodSecurity:            { icon: 'bowl-food', pillar: 'social' },
    genderEquity:            { icon: 'gender-intersex', pillar: 'social' },
    corporateVolunteers:     { icon: 'users', pillar: 'social' },
    heritageSitesSupported:  { icon: 'bank', pillar: 'cultural' },
    intangibleHeritage:      { icon: 'music-notes', pillar: 'cultural' },
    artisansCraftspeople:    { icon: 'diamond', pillar: 'cultural' },
    culturalEventsSupported: { icon: 'megaphone', pillar: 'cultural' },
    culturalEducationReach:  { icon: 'graduation-cap', pillar: 'cultural' },
    // Environmental
    totalGhgEmissions:       { icon: 'cloud', pillar: 'environmental' },
    scope3Emissions:         { icon: 'factory', pillar: 'environmental' },
    renewableEnergyShare:    { icon: 'solar-panel', pillar: 'environmental' },
    fleetFuelConsumption:    { icon: 'gas-pump', pillar: 'environmental' },
    totalWater:              { icon: 'droplet', pillar: 'environmental' },
    totalElectricity:        { icon: 'zap', pillar: 'environmental' },
    wasteDiversionRate:      { icon: 'recycle', pillar: 'environmental' },
    landfillWaste:           { icon: 'trash', pillar: 'environmental' },
    ghgIntensity:            { icon: 'cloud', pillar: 'environmental' },
    energyIntensity:         { icon: 'gauge', pillar: 'environmental' },
    waterIntensity:          { icon: 'droplet', pillar: 'environmental' },
    wasteIntensity:          { icon: 'trash', pillar: 'environmental' },
    // Social — staff
    totalWorkforce:          { icon: 'users', pillar: 'social' },
    genderBalance:           { icon: 'gender-intersex', pillar: 'social' },
    ethnicDiversity:         { icon: 'users-three', pillar: 'social' },
    genderPayEquity:         { icon: 'wallet', pillar: 'social' },
    // Heredaba `icon-governance.svg` (un banco) del mapeo viejo, que ya era
    // incorrecto: un edificio institucional no dice "mujeres en liderazgo". Y con
    // el pilar encima chocaba con `heritageSitesSupported`, que sí es un banco.
    womenInLeadership:       { icon: 'briefcase', pillar: 'social' },
    ageDiversity:            { icon: 'users-four', pillar: 'social' },
    localHiring:             { icon: 'map-pin', pillar: 'social' },
    // Suppliers
    totalSuppliers:          { icon: 'buildings', pillar: 'governance' },
    localSourcing:           { icon: 'map-pin', pillar: 'governance' },
    smeSocialEnterprise:     { icon: 'users-three', pillar: 'governance' },
    localSupplierSpend:      { icon: 'piggy-bank', pillar: 'governance' },
    spendCertifiedSuppliers: { icon: 'receipt', pillar: 'governance' },
    certifiedSuppliers:      { icon: 'seal-check', pillar: 'governance' },
};

export const useHelpers = () => {
    
    const isNumber = (evt) => {
        const keysAllowed = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.'];
        const keyPressed  = evt.key;
        if (!keysAllowed.includes(keyPressed)) {
               evt.preventDefault()
        }
    }

    const formattedDate = (date, full = false) => {
        //return dayjs(date).format(!full ? 'YYYY/MM/DD' : 'MMMM D, YYYY');
        const lang = JSON.parse(localStorage.getItem("gs_lang")) || "en";
        const dateToParse = typeof date === 'string' ? date.replace('Z', '') : date;
        return dayjs(dateToParse)
            .locale(lang)
            .format(!full ? 'YYYY/MM/DD' : lang == 'en' ? 'MMMM D, YYYY' : 'D [de] MMMM [de] YYYY');
    };

    const getLogMeta = (data, key) => {
        if(data == undefined) return "";
        // console.log(data, key);
        const decode = JSON.parse(data);
        return decode[key];
    };

    const formatNumber = (value) => {
        const formatted = new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value ?? 0);

        if (formatted === "NaN") return "0";

        if (formatted.endsWith(".00")) {
            return formatted.replace(".00", "");
        }

        return formatted;
    };

    const formatNumberCurrency = (value) => {
        
        const formattedValue = new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
        }).format(Math.abs(value));

        return value < 0 ? `(${formattedValue})` : formattedValue;
    };

    const countries = [
        { name: "Afghanistan", code: "AF" },
        { name: "Albania", code: "AL" },
        { name: "Algeria", code: "DZ" },
        { name: "Andorra", code: "AD" },
        { name: "Angola", code: "AO" },
        { name: "Antigua and Barbuda", code: "AG" },
        { name: "Argentina", code: "AR" },
        { name: "Armenia", code: "AM" },
        { name: "Australia", code: "AU" },
        { name: "Austria", code: "AT" },
        { name: "Azerbaijan", code: "AZ" },
        { name: "Bahamas", code: "BS" },
        { name: "Bahrain", code: "BH" },
        { name: "Bangladesh", code: "BD" },
        { name: "Barbados", code: "BB" },
        { name: "Belarus", code: "BY" },
        { name: "Belgium", code: "BE" },
        { name: "Belize", code: "BZ" },
        { name: "Benin", code: "BJ" },
        { name: "Bhutan", code: "BT" },
        { name: "Bolivia", code: "BO" },
        { name: "Bosnia and Herzegovina", code: "BA" },
        { name: "Botswana", code: "BW" },
        { name: "Brazil", code: "BR" },
        { name: "Brunei", code: "BN" },
        { name: "Bulgaria", code: "BG" },
        { name: "Burkina Faso", code: "BF" },
        { name: "Burundi", code: "BI" },
        { name: "Cabo Verde", code: "CV" },
        { name: "Cambodia", code: "KH" },
        { name: "Cameroon", code: "CM" },
        { name: "Canada", code: "CA" },
        { name: "Central African Republic", code: "CF" },
        { name: "Chad", code: "TD" },
        { name: "Chile", code: "CL" },
        { name: "China", code: "CN" },
        { name: "Colombia", code: "CO" },
        { name: "Comoros", code: "KM" },
        { name: "Congo, Democratic Republic of the", code: "CD" },
        { name: "Congo, Republic of the", code: "CG" },
        { name: "Costa Rica", code: "CR" },
        { name: "Croatia", code: "HR" },
        { name: "Cuba", code: "CU" },
        { name: "Cyprus", code: "CY" },
        { name: "Czech Republic", code: "CZ" },
        { name: "Denmark", code: "DK" },
        { name: "Djibouti", code: "DJ" },
        { name: "Dominica", code: "DM" },
        { name: "Dominican Republic", code: "DO" },
        { name: "Ecuador", code: "EC" },
        { name: "Egypt", code: "EG" },
        { name: "El Salvador", code: "SV" },
        { name: "Equatorial Guinea", code: "GQ" },
        { name: "Eritrea", code: "ER" },
        { name: "Estonia", code: "EE" },
        { name: "Eswatini", code: "SZ" },
        { name: "Ethiopia", code: "ET" },
        { name: "Fiji", code: "FJ" },
        { name: "Finland", code: "FI" },
        { name: "France", code: "FR" },
        { name: "Gabon", code: "GA" },
        { name: "Gambia", code: "GM" },
        { name: "Georgia", code: "GE" },
        { name: "Germany", code: "DE" },
        { name: "Ghana", code: "GH" },
        { name: "Greece", code: "GR" },
        { name: "Grenada", code: "GD" },
        { name: "Guatemala", code: "GT" },
        { name: "Guinea", code: "GN" },
        { name: "Guinea-Bissau", code: "GW" },
        { name: "Guyana", code: "GY" },
        { name: "Haiti", code: "HT" },
        { name: "Honduras", code: "HN" },
        { name: "Hungary", code: "HU" },
        { name: "Iceland", code: "IS" },
        { name: "India", code: "IN" },
        { name: "Indonesia", code: "ID" },
        { name: "Iran", code: "IR" },
        { name: "Iraq", code: "IQ" },
        { name: "Ireland", code: "IE" },
        { name: "Israel", code: "IL" },
        { name: "Italy", code: "IT" },
        { name: "Jamaica", code: "JM" },
        { name: "Japan", code: "JP" },
        { name: "Jordan", code: "JO" },
        { name: "Kazakhstan", code: "KZ" },
        { name: "Kenya", code: "KE" },
        { name: "Kiribati", code: "KI" },
        { name: "Korea, North", code: "KP" },
        { name: "Korea, South", code: "KR" },
        { name: "Kosovo", code: "XK" },
        { name: "Kuwait", code: "KW" },
        { name: "Kyrgyzstan", code: "KG" },
        { name: "Laos", code: "LA" },
        { name: "Latvia", code: "LV" },
        { name: "Lebanon", code: "LB" },
        { name: "Lesotho", code: "LS" },
        { name: "Liberia", code: "LR" },
        { name: "Libya", code: "LY" },
        { name: "Liechtenstein", code: "LI" },
        { name: "Lithuania", code: "LT" },
        { name: "Luxembourg", code: "LU" },
        { name: "Madagascar", code: "MG" },
        { name: "Malawi", code: "MW" },
        { name: "Malaysia", code: "MY" },
        { name: "Maldives", code: "MV" },
        { name: "Mali", code: "ML" },
        { name: "Malta", code: "MT" },
        { name: "Marshall Islands", code: "MH" },
        { name: "Mauritania", code: "MR" },
        { name: "Mauritius", code: "MU" },
        { name: "Mexico", code: "MX" },
        { name: "Micronesia", code: "FM" },
        { name: "Moldova", code: "MD" },
        { name: "Monaco", code: "MC" },
        { name: "Mongolia", code: "MN" },
        { name: "Montenegro", code: "ME" },
        { name: "Morocco", code: "MA" },
        { name: "Mozambique", code: "MZ" },
        { name: "Myanmar", code: "MM" },
        { name: "Namibia", code: "NA" },
        { name: "Nauru", code: "NR" },
        { name: "Nepal", code: "NP" },
        { name: "Netherlands", code: "NL" },
        { name: "New Zealand", code: "NZ" },
        { name: "Nicaragua", code: "NI" },
        { name: "Niger", code: "NE" },
        { name: "Nigeria", code: "NG" },
        { name: "North Macedonia", code: "MK" },
        { name: "Norway", code: "NO" },
        { name: "Oman", code: "OM" },
        { name: "Pakistan", code: "PK" },
        { name: "Palau", code: "PW" },
        { name: "Palestine", code: "PS" },
        { name: "Panama", code: "PA" },
        { name: "Papua New Guinea", code: "PG" },
        { name: "Paraguay", code: "PY" },
        { name: "Peru", code: "PE" },
        { name: "Philippines", code: "PH" },
        { name: "Poland", code: "PL" },
        { name: "Portugal", code: "PT" },
        { name: "Puerto Rico", code: "PR" },
        { name: "Qatar", code: "QA" },
        { name: "Romania", code: "RO" },
        { name: "Russia", code: "RU" },
        { name: "Rwanda", code: "RW" },
        { name: "Saint Kitts and Nevis", code: "KN" },
        { name: "Saint Lucia", code: "LC" },
        { name: "Saint Vincent and the Grenadines", code: "VC" },
        { name: "Samoa", code: "WS" },
        { name: "San Marino", code: "SM" },
        { name: "Sao Tome and Principe", code: "ST" },
        { name: "Saudi Arabia", code: "SA" },
        { name: "Senegal", code: "SN" },
        { name: "Serbia", code: "RS" },
        { name: "Seychelles", code: "SC" },
        { name: "Sierra Leone", code: "SL" },
        { name: "Singapore", code: "SG" },
        { name: "Slovakia", code: "SK" },
        { name: "Slovenia", code: "SI" },
        { name: "Solomon Islands", code: "SB" },
        { name: "Somalia", code: "SO" },
        { name: "South Africa", code: "ZA" },
        { name: "South Sudan", code: "SS" },
        { name: "Spain", code: "ES" },
        { name: "Sri Lanka", code: "LK" },
        { name: "Sudan", code: "SD" },
        { name: "Suriname", code: "SR" },
        { name: "Sweden", code: "SE" },
        { name: "Switzerland", code: "CH" },
        { name: "Syria", code: "SY" },
        { name: "Taiwan", code: "TW" },
        { name: "Tajikistan", code: "TJ" },
        { name: "Tanzania", code: "TZ" },
        { name: "Thailand", code: "TH" },
        { name: "Timor-Leste", code: "TL" },
        { name: "Togo", code: "TG" },
        { name: "Tonga", code: "TO" },
        { name: "Trinidad and Tobago", code: "TT" },
        { name: "Tunisia", code: "TN" },
        { name: "Turkey", code: "TR" },
        { name: "Turkmenistan", code: "TM" },
        { name: "Tuvalu", code: "TV" },
        { name: "Uganda", code: "UG" },
        { name: "Ukraine", code: "UA" },
        { name: "United Arab Emirates", code: "AE" },
        { name: "United Kingdom", code: "GB" },
        { name: "United States", code: "US" },
        { name: "Uruguay", code: "UY" },
        { name: "Uzbekistan", code: "UZ" },
        { name: "Vanuatu", code: "VU" },
        { name: "Vatican City", code: "VA" },
        { name: "Venezuela", code: "VE" },
        { name: "Vietnam", code: "VN" },
        { name: "Yemen", code: "YE" },
        { name: "Zambia", code: "ZM" },
        { name: "Zimbabwe", code: "ZW" },
    ];

    const industries = [
        "Agriculture and Agribusiness",
        "Automotive and Transportation",
        "Construction and Building",
        "Energy (including fossil fuels, renewables, and utilities)",
        "Manufacturing and Production",
        "Mining and Extraction",
        "Technology and Electronics",
        "Healthcare and Pharmaceuticals",
        "Retail and Consumer Goods",
        "Food and Beverage",
        "Hospitality and Tourism",
        "Financial Services and Banking",
        "Education and Research",
        "Government and Public Services",
        "Entertainment and Media",
        "Telecommunications",
        "Aerospace and Defense",
        "Non-profit and NGO",
        "Real Estate and Property",
        "Other (for industries not listed above)",
    ];

    const griQuestions = [
        {
            code: 'GRI101',
            questions: ["101-1", "101-2", "101-3", "101-4", "101-5", "101-6", "101-7", "101-8"]
        },
        {
            code: 'GRI201',
            questions: ["201-1", "201-2", "201-3", "201-4"]
        },
        {
            code: 'GRI202',
            questions: ["202-1", "202-2"]
        },
        {
            code: 'GRI203',
            questions: ["203-1", "203-2"]
        },
        {
            code: 'GRI204',
            questions: ["204-1"]
        },
        {
            code: 'GRI205',
            questions: ["205-1", "205-2", "205-3"]
        },
        {
            code: 'GRI206',
            questions: ["206-1"]
        },
        {
            code: 'GRI207',
            questions: ["207-1", "207-2", "207-3", "207-4"]
        },
        {
            code: 'GRI301',
            questions: ["301-1", "301-2", "301-3"]
        },
        {
            code: 'GRI302',
            questions: ["302-1", "302-2", "302-3", "302-4", "302-5"]
        },
        {
            code: 'GRI303',
            questions: ["303-1", "303-2", "303-3", "303-4", "303-5"]
        },
        {
            code: 'GRI304',
            questions: ["304-1", "304-2", "304-3", "304-4"]
        },
        {
            code: 'GRI305',
            questions: ["305-1", "305-2", "305-3", "305-4", "305-5", "305-6", "305-7"]
        },
        {
            code: 'GRI306',
            questions: ["306-1"]
        },
        {
            code: 'GRI307',
            questions: ["306-1", "306-2", "306-3", "306-4", "306-5"]
        },
        {
            code: 'GRI308',
            questions: ["308-1", "308-2"]
        },
        {
            code: 'GRI401',
            questions: ["401-1", "401-2", "401-3"]
        },
        {
            code: 'GRI402',
            questions: ["402-1"]
        },
        {
            code: 'GRI403',
            questions: ["403-1", "403-2", "403-3", "403-4", "403-5", "403-6", "403-7", "403-8", "403-9", "403-10"]
        },
        {
            code: 'GRI404',
            questions: ["404-1", "404-2", "404-3"]
        },
        {
            code: 'GRI405',
            questions: ["405-1", "405-2"]
        },
        {
            code: 'GRI406',
            questions: ["406-1"]
        },
        {
            code: 'GRI407',
            questions: ["407-1"]
        },
        {
            code: 'GRI408',
            questions: ["408-1"]
        },
        {
            code: 'GRI409',
            questions: ["409-1"]
        },
        {
            code: 'GRI410',
            questions: ["410-1"]
        },
        {
            code: 'GRI411',
            questions: ["411-1"]
        },
        {
            code: 'GRI413',
            questions: ["413-1", "413-2"]
        },
        {
            code: 'GRI414',
            questions: ["414-1", "414-2"]
        },
        {
            code: 'GRI415',
            questions: ["415-1"]
        },
        {
            code: 'GRI416',
            questions: ["416-1", "416-2"]
        },
        {
            code: 'GRI417',
            questions: ["417-1", "417-2", "417-3"]
        },
        {
            code: 'GRI418',
            questions: ["418-1"]
        }
    ];
    
    const validFormInAccordence = (model, extraKeys = []) => {
        const exceptions = [
            'paticipanLideres', 
        ];
  
        let keysToValidate = ["2-1", "2-2", "2-3", "2-4", "2-5"];
        keysToValidate = keysToValidate.concat(extraKeys);
        // console.log('keysToValidate', keysToValidate);
        for (const key in model) {
          if (Object.prototype.hasOwnProperty.call(model, key)) {
            if (!keysToValidate.includes(key)) continue;
  
            const section = model[key];
            
            // Check omitted details if status is 1
            if (section.omitted && section.status === 1) {
                if (!section.omitted.details || section.omitted.details.trim() === "") {
                  return key; // Invalid if omitted.details is empty
                }
                // If omitted.details is not empty, skip further validations for this section
                continue; 
            } 

            if (!section.responses) continue;
            
            for (const responseKey in section.responses) {
              if (Object.prototype.hasOwnProperty.call(section.responses, responseKey)) {
                const value = section.responses[responseKey];
  
                if (Array.isArray(value)) {
                  if (value.length === 0) {
                    return key;
                  }
                } else {
                    console.log('key', key);
                    console.log('responseKey', responseKey);
                    console.log('value', value);
                    if(typeof value == "string"){
                        const strippedValue = value.replace(/<\/?[^>]+(>|$)/g, "").trim();
                        const isEmptyValue = (strippedValue === "" || strippedValue === null || strippedValue === undefined);
                        
                        if (isEmptyValue && !exceptions.includes(responseKey)) {
                            return key;
                        }
                    }
                    // Valida 2-2 entities 
                    if (responseKey == 'empresaTieneEntidades' &&  value == 1) {
                        const empresaEntidadesDetalle = section.responses['empresaEntidadesDetalle'];
                        
                        if (!empresaEntidadesDetalle || !Array.isArray(empresaEntidadesDetalle) || empresaEntidadesDetalle.length === 0) {
                            return key;
                        }

                        for (const entidad of empresaEntidadesDetalle) {
                            if (!entidad.name || !entidad.diferencias || !entidad.enfoqueAdoptado) {
                                return key;
                            }
                        }
                    }

                    // // valida 2-3 
                    // if (responseKey == 'reporteAlineaEstadosFinancieros' &&  value == 1) {
                    //     const reporteName = section.responses['reporteName'];
                    //     const reportePuesto = section.responses['reportePuesto'];
                    //     const reporteCorreo = section.responses['reporteCorreo'];
                    //     const reporteFechaPublicacion = section.responses['reporteFechaPublicacion'];
                    //     if (reporteName.trim() == '' || reportePuesto.trim() == '' || 
                    //         reporteCorreo.trim() == '' || reporteFechaPublicacion.trim() == '') {
                    //         return key;
                    //     }
                    // } else if(responseKey == 'reporteAlineaEstadosFinancieros' &&  value == 0) {
                    //     const resp = section.responses['reporteNoAlinea'];
                    //     const strippedValue = resp.replace(/<\/?[^>]+(>|$)/g, "").trim();
                    //     if (strippedValue == '') {
                    //         return key;
                    //     }
                    // }

                    //  // valida 2-5 Q2_2_5
                    //  if (responseKey == 'Q2_2_5' &&  value == 1) {
                    //     let paticipanLideres = section.responses['paticipanLideres'].replace(/<\/?[^>]+(>|$)/g, "").trim();
                    //     let verificacionExternaQ3 = section.responses['verificacionExternaQ3'];
                    //     let verificacionExternaQ3_yes = section.responses['verificacionExternaQ3_yes'].replace(/<\/?[^>]+(>|$)/g, "").trim();
                    //     let verificacionExternaQ4 = section.responses['verificacionExternaQ4'].replace(/<\/?[^>]+(>|$)/g, "").trim();
                    //     let verificacionExternaQ5 = section.responses['verificacionExternaQ5'].replace(/<\/?[^>]+(>|$)/g, "").trim();

                    //     if (paticipanLideres.trim() == ''  || 
                    //     verificacionExternaQ3 == '' ||
                    //     verificacionExternaQ4.trim() == '' ||
                    //     verificacionExternaQ5.trim() == '' ||
                    //     (verificacionExternaQ3 == 1 && verificacionExternaQ3_yes == '' )) {
                    //         return key;
                    //     }
                    // }

                }
              }
            }
            
          }
        }
        return true; 
    }

    const generateYearOptions = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = 2019; i <= currentYear; i++) {
            years.push(i);
        }
        return years.reverse();
    };

    const sourceTypesData = [
        {
            code: 'power',
            name: 'Power'
        },
        {
            code: 'ownedVehicles',
            name: 'Owned Vehicles'
        },
        {
            code: 'rentedVehicles',
            name: 'Leased/Rented Vehicles'
        },
        {
            code: 'flights',
            name: 'Flights'
        },
        {
            code: 'lubricants',
            name: 'Lubricants'
        },
        {
            code: 'combustion',
            name: 'Stationary Combustion'
        },
        {
            code: 'landfill',
            name: 'Landfill'
        },
        {
            code: 'composting',
            name: 'Composting'
        },
        {
            code: 'recycling',
            name: 'Recycling'
        },
        {
            code: 'septic',
            name: 'Septic'
        },
        {
            code: 'wasteManagment',
            name: 'Wastewater'
        },
        {
            code: 'treamentPlant',
            name: 'Treatment Plant'
        },
        {
            code: 'leaks',
            name: 'Refigerants and others'
        },
        {
            code: 'emissions',
            name: 'Other Emissions'
        },
        {   
            code: "renewableSources",
            name: "Renewable Sources"
        },
        {   
            code: "noRenewableSources",
            name: "Non-Renewable Sources"
        }
    ];
    
    const combustionFuelOptions = [
        "Butane",
        "CNG",
        "LNG",
        "LPG",
        "Natural gas",
        "Natural gas (100% mineral blend)",
        "Other petroleum gas",
        "Propane",
        "Aviation spirit",
        "Aviation turbine fuel",
        "Burning oil",
        "Diesel",
        "Diesel (100% mineral diesel)",
        "Fuel oil",
        "Gas oil",
        "Lubricants",
        "Naphtha",
        "Petrol",
        "Petrol (100% mineral petrol)",
        "Processed fuel oils - residual oil",
        "Processed fuel oils - distillate oil",
        "Refinery miscellaneous",
        "Waste oils",
        "Marine gas oil",
        "Marine fuel oil",
        "Coal (industrial)",
        "Coal (electricity generation)",
        "Coal (domestic)",
        "Coking coal",
        "Petroleum coke",
        "Coal (electricity generation - home produced coal only)",
    ];

    const combustionEPAFuelOptions = [
        "Biodiesel",
        "Diesel",
        "LPG",
        "Gasoline",
    ];

    const wasteTypeData = [
        "Aggregates",
        "Average construction",
        "Asbestos",
        "Asphalt",
        "Bricks",
        "Concrete",
        "Insulation",
        "Metals",
        "Soils",
        "Mineral oil",
        "Plasterboard",
        "Tyres",
        "Wood",
        "Books",
        "Glass",
        "Clothing",
        "Household residual waste",
        "Organic-food and drink waste",
        "Organic-garden waste",
        "Organic-mixed food and garden waste",
        "Commercial and industrial waste",
        "WEEE-fridges and freezers",
        "WEEE-large",
        "WEEE-mixed",
        "WEEE-small",
        "Batteries",
        "Metal-aluminium cans and foil (excl. forming)",
        "Metal-mixed cans",
        "Metal-scrap metal",
        "Metal-steel cans",
        "Plastics-average plastics",
        "Plastics-average plastic film",
        "Plastics-average plastic rigid",
        "Plastics-HDPE (incl. forming)",
        "Plastics-LDPE and LLDPE (incl. forming)",
        "Plastics-PET (incl. forming)",
        "Plastics-PP (incl. forming)",
        "Plastics-PS (incl. forming)",
        "Plastics-PVC (incl. forming)",
        "Paper and board-board",
        "Paper and board-mixed",
        "Paper and board-paper",
    ];

    const typeOfArragmentData = ["Open-loop","Closed-loop","Combustion","Anaerobic digestion"];

    const flightDataTypes = ["Domestic", "Short-haul", "Long-haul", "International"];
    const flightDataClass = ["Average passenger", "Economy class", "Premium economy class", "Business class", "First class"];

    const capitalizeFirstLetter = (str) => {
        if (!str) return str; // Verifica si la cadena está vacía
        return str.charAt(0).toUpperCase() + str.slice(1); // Capitaliza la primera letra
    };

    // Busca un país por su nombre en español o inglés tal como lo da el navegador.
    // El índice se arma una sola vez (la lista no cambia).
    const normalize = (t) => String(t).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    let localNameIndex = null;
    const findCountryByLocalName = (name) => {
        if (!localNameIndex) {
            localNameIndex = new Map();
            for (const lang of ['es', 'en']) {
                let dn = null;
                try { dn = new Intl.DisplayNames([lang], { type: 'region' }); } catch { dn = null; }
                if (!dn) continue;
                for (const item of countries) {
                    try { const n = dn.of(item.code); if (n) localNameIndex.set(normalize(n), item); } catch { /* código no válido para Intl */ }
                }
            }
            for (const item of countries) localNameIndex.set(normalize(item.name), item);
        }
        return localNameIndex.get(normalize(name)) || null;
    };

    // Nombre del país en el idioma activo. `companies.location` guarda el
    // código ISO ("MX"); hay filas viejas con el nombre en inglés ("Mexico"),
    // que también se aceptan. `countries` solo tiene nombres en inglés: la
    // traducción la da el navegador (Intl.DisplayNames), sin librería. Si no
    // está disponible, queda el nombre en inglés de la lista.
    // Lee `i18n.global.locale`, así que es reactivo al cambio de idioma.
    // También hay filas con el nombre en español ("México", "Belice"): se buscan
    // contra el nombre que da el navegador en es, sin tildes ni mayúsculas.
    const getCountryName = (code) => {
        if (!code) return null;
        const v = String(code).trim();
        const country = countries.find((item) => item.code === v.toUpperCase())
            || countries.find((item) => item.name.toLowerCase() === v.toLowerCase())
            || findCountryByLocalName(v);
        if (!country) return null;
        const lang = String(i18n.global.locale.value || 'en').slice(0, 2);
        try {
            return new Intl.DisplayNames([lang], { type: 'region' }).of(country.code) || country.name;
        } catch {
            return country.name;
        }
    };
    
    const wasteMaterials = [
        { code: "metals", types: ["landfill"] },
        { code: "glass", types: ["landfill", "recycling"] },
        { code: "plastics", types: ["landfill"] },
        { code: "mixed-organics", types: ["landfill", "composting"] },
        { code: "mixed-recyclables", types: ["landfill", "recycling"] },
        { code: "food-waste", types: ["landfill", "composting"] },
        { code: "electronics-e-waste", types: ["landfill", "recycling"] },
        { code: "mixed-municipal-solid-waste", types: ["landfill"] },
        { code: "corrugated-containers",  types: ["landfill", "recycling"] },
        { code: "yard-trimmings", types: ["landfill"] },
        { code: "mixed-paper-general", types: ["landfill", "recycling"] },
        { code: "tires", types: ["landfill", "recycling"] },
        { code: "textiles", types: ["landfill"] },
        { code: "construction-demolition", types: ["landfill"] },
        { code: "garden-waste", types: ["composting"] },
        { code: "material", types: ["recycling"] },
        { code: "mixed-metals", types: ["recycling"] },
        { code: "mixed-plastics", types: ["recycling"] },
        { code: "aluminum-cans", types: ["recycling"] },
        { code: "aluminum-ingot", types: ["recycling"] },
        { code: "steel-cans", types: ["recycling"] },
        { code: "copper-wire", types: ["recycling"] },
        { code: "hdpe", types: ["recycling"] },
        { code: "pet", types: ["recycling"] },
        { code: "pp", types: ["recycling"] },
        { code: "magazines-newspaper-office-paper", types: ["recycling"] },
        { code: "phonebooks-textbooks", types: ["recycling"] },
        { code: "mixed-paper-offices",  types: ["recycling"] },
        { code: "desktop-cpus", types: ["recycling"] },
        { code: "portable-electronic-devices", types: ["recycling"] },
        { code: "flat-panel-displays", types: ["recycling"] },
        { code: "concrete", types: ["recycling"] },
        { code: "asphalt-concrete", types: ["recycling"] },
        { code: "structural-steel", types: ["recycling"] },
    ];

    const getIconPath = (key, subdir = "images") => {
        try {
            // Load all image files recursively from /assets and its subfolders
            const images = require.context("@/assets", true, /\.(png|jpe?g|svg|webp)$/);

            // Build the relative path (e.g. "./images/icons/check.svg")
            const path = `./${subdir}/${key}`;

            // Check if the image exists in the context
            if (images.keys().includes(path)) {
                return images(path);
            } else {
                console.warn(`Image not found: ${path}`);
            }
        } catch (err) {
            console.error("Error loading image:", err);
        }
    };

    const openAIAssistant = (context = null) => {
        _aiContext.value = context || null;
        const aiButton = document.getElementById('OpenAIAssistent');
        if (aiButton) {
            aiButton.click();
        } else {
            console.warn('AI Button not found');
        }
    };

    // Quarter helper functions
    const getQuarterDates = (quarter) => {
        const year = new Date().getFullYear();
        const quarters = {
            1: [new Date(year, 0, 1), new Date(year, 2, 31)],   // Q1: Jan-Mar
            2: [new Date(year, 3, 1), new Date(year, 5, 30)],   // Q2: Apr-Jun
            3: [new Date(year, 6, 1), new Date(year, 8, 30)],   // Q3: Jul-Sep
            4: [new Date(year, 9, 1), new Date(year, 11, 31)]   // Q4: Oct-Dec
        };
        return quarters[quarter];
    };

    const getCurrentQuarter = () => {
        const month = new Date().getMonth();
        if (month < 3) return 1;
        if (month < 6) return 2;
        if (month < 9) return 3;
        return 4;
    };

    // Date range presets for filters
    const getDatePresets = (t) => {
        return [
            {
                label: t('TODAY'),
                value: [new Date(), new Date()]
            },
            {
                label: t('THIS_MONTH'),
                value: [
                    new Date(new Date().getFullYear(), new Date().getMonth(), 1),
                    new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
                ]
            },
            {
                label: t('LAST_MONTH'),
                value: [
                    new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
                    new Date(new Date().getFullYear(), new Date().getMonth(), 0)
                ]
            },
            {
                label: t('QUARTER_1'),
                value: getQuarterDates(1)
            },
            {
                label: t('QUARTER_2'),
                value: getQuarterDates(2)
            },
            {
                label: t('QUARTER_3'),
                value: getQuarterDates(3)
            },
            {
                label: t('QUARTER_4'),
                value: getQuarterDates(4)
            }
        ];
    };

    return {
        countries,
        industries,
        griQuestions,
        wasteMaterials,
        sourceTypesData,
        wasteTypeData,
        flightDataTypes,
        flightDataClass,
        typeOfArragmentData,
        combustionFuelOptions,
        combustionEPAFuelOptions,
        // !Methods
        getIconPath,
        isNumber,
        getLogMeta,
        formattedDate,
        getCountryName,
        formatNumber,
        formatNumberCurrency,
        validFormInAccordence,
        generateYearOptions,
        capitalizeFirstLetter,
        openAIAssistant,
        aiContext: _aiContext,
        // Date/Quarter helpers
        getQuarterDates,
        getCurrentQuarter,
        getDatePresets
    }

}